import React from 'react';
import { SortableHandle } from 'react-sortable-hoc';
import {
  RiDeleteBinLine,
  RiDownload2Line,
  RiMoreFill,
  RiEyeLine,
  RiEyeOffLine,
  RiChat4Line,
  RiChatOffLine,
  RiCameraOffLine,
  RiCameraLine,
  RiHammerLine,
  RiIndeterminateCircleLine,
  InputCheckBox
} from '@itandi/itandi-bb-ui';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import { useOpenHandler } from '~/hooks/useOpenHandler';
import Styles from './ReportImagesAreaBlock.module.scss';
import type { DealReportImage } from '~/models/DealReportImage';
import {
  operableReportImage,
  imageUploadedLabel
} from '~/models/DealReportImage';
import { ImagePreviewModal } from '~/components/common/parts/modal/ImagePreviewModal';
import { ReportImagesAreaBlockRemarks } from '~/components/common/reports/ReportImagesAreaBlockRemarks';
import type { NskResponse } from '../../../models/Api';
import { Dropdown } from '~/components/common/parts/dropdown/Dropdown';
import { DropdownMenu } from '~/components/common/parts/dropdown/DropdownMenu';
import { DropdownList } from '../parts/dropdown/DropdownList';
import { ConfirmModal } from '~/components/common/parts/modal/ConfirmModal';
import { isSuccess } from '~/utils/api/api_handler';

type Props = {
  reportImage: DealReportImage;
  handleDownloadImage: (reportImage: DealReportImage) => void;
  handleMutateReportImages: () => Promise<void>;
  handleUpdateSeq:
    | ((newOrderImages: DealReportImage[]) => Promise<void>)
    | null;
  handleUpdate: (submitValue: DealReportImage) => Promise<NskResponse<void>>;
  handleDestroy:
    | ((reportImage: DealReportImage) => Promise<NskResponse<void>>)
    | null;
  companyType: 'company' | 'vendor';
  displaySeqNo: number | null;
  onChangeSelectedImage: (reportImageId: number) => void;
  selectedImageIds: ReadonlyArray<number>;
};

const DragHandle = SortableHandle(() => (
  <div className={Styles.DragHandleWrapper}>
    <img alt="drag handle" src="/assets/DragHandle.svg" />
  </div>
));

export const ReportImagesAreaBlock: React.FC<Props> = ({
  reportImage,
  handleDownloadImage,
  handleMutateReportImages,
  handleUpdateSeq,
  handleUpdate,
  handleDestroy,
  companyType,
  displaySeqNo,
  onChangeSelectedImage,
  selectedImageIds
}: Props) => {
  const { isOpen, handleOpen, handleClose } = useOpenHandler();
  const {
    isOpen: isOpenDelete,
    handleOpen: handleOpenDelete,
    handleClose: handleCloseDelete
  } = useOpenHandler();
  const onClickImage = (e: React.MouseEvent): void => {
    e.preventDefault();
    handleOpen();
  };
  const isVendor = companyType === 'vendor';
  const operableImage = operableReportImage(reportImage, companyType);
  const [viewReportImage, setViewReportImage] =
    React.useState<DealReportImage>(reportImage);
  const isShared = viewReportImage?.shared;
  const isOutputPdfImage = viewReportImage?.outputPdfImage;
  const isOutputPdfNote = viewReportImage?.outputPdfNote;
  const uploadLabel = imageUploadedLabel(isVendor, reportImage);
  const handleAcceptDestroy = async (): Promise<void> => {
    if (handleDestroy === null) {
      return;
    }
    const result = await handleDestroy(reportImage);
    if (isSuccess(result)) {
      await handleMutateReportImages?.();
      toast.success('削除しました');
      return;
    }
    toast.error('エラーが発生しました');
  };
  const handleAcceptUpdateShared = async (
    submitValue: boolean
  ): Promise<void> => {
    const newReportImage = { ...reportImage, shared: submitValue };
    await handleUpdate(newReportImage);
    setViewReportImage(newReportImage);
  };
  const handleAcceptUpdateOutputPdf = async (
    submitKey: 'outputPdfImage' | 'outputPdfNote',
    submitValue: boolean
  ): Promise<void> => {
    const newReportImage = { ...reportImage, [submitKey]: submitValue };
    await handleUpdate(newReportImage);
    await handleMutateReportImages?.();
  };
  const handleAcceptUpdateConstruction = async (
    submitValue: 'unspecified' | 'targeted'
  ): Promise<void> => {
    await handleUpdate({ ...reportImage, construction: submitValue });
    await handleMutateReportImages?.();
  };
  return (
    <div
      className={clsx(
        Styles.ImageBlockWrapper,
        reportImage.construction === 'targeted' && Styles.Targeted
      )}
    >
      <div className={Styles.ImageBlock}>
        {handleUpdateSeq !== null && <DragHandle />}
        <div className={Styles.ImageWrapper}>
          <a href="" onClick={onClickImage}>
            <img
              alt="写真"
              className={Styles.Image}
              src={
                reportImage.thumbnailImageUrl === null
                  ? reportImage.imageUrl
                  : reportImage.thumbnailImageUrl
              }
            />
          </a>
          <div className={Styles.Plates}>
            {(isVendor || (!isVendor && isOutputPdfImage)) && (
              <div className={Styles.Plate}>No.{displaySeqNo}</div>
            )}
            {uploadLabel && <div className={Styles.Plate}>{uploadLabel}</div>}
          </div>
          {isVendor && !isShared && (
            <p className={Styles.NotSharedPlate}>管理会社に共有されません</p>
          )}
          {!isVendor && !isOutputPdfImage && (
            <p className={Styles.NotOutPutPdf}>写真は報告書に出力されません</p>
          )}
          <p className={Styles.ImageCheckbox}>
            <InputCheckBox
              checked={selectedImageIds.includes(reportImage.id)}
              onChange={(): void => onChangeSelectedImage(reportImage.id)}
            />
          </p>
          <div className={Styles.ButtonWrapper}>
            <Dropdown
              className={Styles.MenuDropdown}
              noBorder
              placement="bottom-start"
              title={
                <span className={Styles.MenuButton}>
                  <RiMoreFill className={Styles.MenuIcon} />
                </span>
              }
              useCaret={false}
              variant="link"
            >
              <DropdownList>
                <DropdownMenu
                  onClick={(): void => handleDownloadImage(reportImage)}
                >
                  <RiDownload2Line className={Styles.DropdownMenuIcon} />
                  画像をダウンロード
                </DropdownMenu>
                {operableImage && (
                  <DropdownMenu
                    onClick={(): void => {
                      handleAcceptUpdateConstruction(
                        viewReportImage?.construction === 'targeted'
                          ? 'unspecified'
                          : 'targeted'
                      ).then();
                    }}
                  >
                    {viewReportImage?.construction === 'targeted' ? (
                      <>
                        <RiIndeterminateCircleLine
                          className={Styles.DropdownMenuIcon}
                        />
                        {reportImage.type === 'DealInspectionReportImage' ? (
                          <>施工推奨から外す</>
                        ) : (
                          <>施工対象から外す</>
                        )}
                      </>
                    ) : (
                      <>
                        <RiHammerLine className={Styles.DropdownMenuIcon} />
                        {reportImage.type === 'DealInspectionReportImage' ? (
                          <>施工推奨にする</>
                        ) : (
                          <>施工対象にする</>
                        )}
                      </>
                    )}
                  </DropdownMenu>
                )}
                {isVendor && operableImage && (
                  <DropdownMenu
                    onClick={(): void => {
                      handleAcceptUpdateShared(!viewReportImage?.shared);
                    }}
                  >
                    {isShared ? (
                      <>
                        <RiEyeOffLine className={Styles.DropdownMenuIcon} />
                        写真を共有しない
                      </>
                    ) : (
                      <>
                        <RiEyeLine className={Styles.DropdownMenuIcon} />
                        写真を共有する
                      </>
                    )}
                  </DropdownMenu>
                )}
                {!isVendor && (
                  <DropdownMenu
                    onClick={(): void => {
                      handleAcceptUpdateOutputPdf(
                        'outputPdfImage',
                        !viewReportImage?.outputPdfImage
                      ).then();
                    }}
                  >
                    {isOutputPdfImage ? (
                      <>
                        <RiCameraOffLine className={Styles.DropdownMenuIcon} />
                        写真を報告書に含めない
                      </>
                    ) : (
                      <>
                        <RiCameraLine className={Styles.DropdownMenuIcon} />
                        写真を報告書に含める
                      </>
                    )}
                  </DropdownMenu>
                )}
                {!isVendor && (
                  <DropdownMenu
                    className={!isOutputPdfImage ? Styles.disabled : ''}
                    onClick={(): void => {
                      handleAcceptUpdateOutputPdf(
                        'outputPdfNote',
                        !viewReportImage?.outputPdfNote
                      ).then();
                    }}
                  >
                    {isOutputPdfNote ? (
                      <>
                        <RiChatOffLine className={Styles.DropdownMenuIcon} />
                        備考を報告書に含めない
                      </>
                    ) : (
                      <>
                        <RiChat4Line className={Styles.DropdownMenuIcon} />
                        備考を報告書に含める
                      </>
                    )}
                  </DropdownMenu>
                )}
                {operableImage && (
                  <DropdownMenu onClick={handleOpenDelete}>
                    <span className={Styles.DeleteLink}>
                      <RiDeleteBinLine className={Styles.DropdownMenuIcon} />
                      画像を削除
                    </span>
                  </DropdownMenu>
                )}
              </DropdownList>
            </Dropdown>
          </div>
          <ConfirmModal
            confirmType="delete"
            handleAccept={handleAcceptDestroy}
            handleCancel={handleCloseDelete}
            isOpen={isOpenDelete}
          >
            この画像を削除してよろしいですか？
          </ConfirmModal>
          <ImagePreviewModal
            handleClose={handleClose}
            isOpen={isOpen}
            reportImage={reportImage}
          />
        </div>
        <div>
          <ReportImagesAreaBlockRemarks
            companyType={companyType}
            handleMutateReportImages={handleMutateReportImages}
            handleUpdate={handleUpdate}
            reportImage={reportImage}
          />
        </div>
      </div>
    </div>
  );
};
